import { redirect, type LoaderFunctionArgs, json } from '@remix-run/cloudflare';
import { useLoaderData } from '@remix-run/react';
import { useCallback } from 'react';
import { sanitizeRedirect } from './api.sessions';
import { Button, type ButtonProps } from '~/components/ui/Button';
import { LoadingDots } from '~/components/ui/LoadingDots';
import { extendRequestContext } from '~/lib/.server/request-context';
import { isAuthenticated } from '~/lib/.server/sessions';
import { setUser } from '~/lib/.server/tracing';
import { useLogin } from '~/lib/login';
import { PRIVACY_POLICY_URL, TERMS_OF_SERVICE_URL } from '~/lib/urls';
import { isMobile } from '~/utils/breakpoints';

type OauthProvider = 'github' | 'google_oauth2' | 'login_password';

export async function loader({ request, context }: LoaderFunctionArgs) {
  const { session, response } = await isAuthenticated(request, context.cloudflare);

  setUser(session);
  extendRequestContext({ session });

  const url = new URL(request.url);
  const next = url.searchParams.get('next');
  const redirectUrl = (next && sanitizeRedirect(next)) ?? '/';

  if (session != null) {
    return redirect(redirectUrl, response);
  }

  return json({ next: redirectUrl }, response);
}

export default function Login() {
  const { next } = useLoaderData<typeof loader>();

  return (
    <div className="relative min-h-screen flex items-center justify-center">
      <div className="max-w-xs flex flex-col items-center w-full">
        <div>
          <h2 className="text-center text-4xl mb-8 font-semibold text-bolt-elements-textPrimary">Login to Bolt</h2>
        </div>

        <LoginButton
          className="min-w-20"
          size="md"
          variant="secondary"
          onLoginSuccess={() => {
            if (next) {
              window.location.href = next;
            }
          }}
        />

        <div className="absolute flex bottom-8 left-[50%] translate-x-[-50%] flex items-center gap-1.5 px-2.5 py-2 text-sm text-center text-bolt-elements-textSecondary bg-bolt-elements-background-depth-2 border border-bolt-elements-borderColor rounded-lg">
          <span className="i-ph:info inline-block text-lg" />
          <p className="text-sm text-bolt-ds-textTertiary">
            By signing in, you accept the{' '}
            <a
              href={TERMS_OF_SERVICE_URL}
              className="hover:text-bolt-elements-textPrimary transition-theme underline"
              target="_blank"
            >
              Terms of Service
            </a>{' '}
            and acknowledge our{' '}
            <a
              href={PRIVACY_POLICY_URL}
              className="hover:text-bolt-elements-textPrimary transition-theme underline"
              target="_blank"
            >
              Privacy Policy
            </a>
            .
          </p>
        </div>
      </div>
    </div>
  );
}

interface LoginButtonProps extends Omit<ButtonProps, 'onClick' | 'disabled'> {
  className?: string;
  onLoginSuccess?: () => void;
  samePageLogin?: boolean;
  provider?: OauthProvider;
}

export function LoginButton({
  children,
  className,
  onLoginSuccess,
  samePageLogin,
  provider,
  startIcon,
  ...rest
}: LoginButtonProps) {
  const { pending, login } = useLogin();
  const shouldUseSamePageLogin = isMobile() || samePageLogin;

  const attemptLogin = useCallback(async () => {
    const url = new URL(window.location.href);
    const destination = shouldUseSamePageLogin ? url.toString().slice(url.origin.length) : undefined;

    if (await login(destination, provider)) {
      onLoginSuccess?.();
    }
  }, [onLoginSuccess, shouldUseSamePageLogin]);

  return (
    <Button
      className={className}
      disabled={pending}
      onClick={attemptLogin}
      startIcon={pending ? 'i-svg-spinners:90-ring-with-bg' : startIcon}
      {...rest}
    >
      {pending ? (
        <span>
          {shouldUseSamePageLogin ? <LoadingDots text="Redirecting" /> : <LoadingDots text="Authenticating" />}
        </span>
      ) : (
        children ?? 'Sign in'
      )}
    </Button>
  );
}
